import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import objStr from 'obj-str';
import useTranslation from 'next-translate/useTranslation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faPhone } from '@fortawesome/free-solid-svg-icons';
import { useBrowserLayoutEffect } from '@/hooks/useBrowserLayoutEffect';
import { APP_ROUTES } from '@constants/constants';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { FullPageScrollPane } from '@/components/FullPageScrollPane';
import templateDataService from '@/services/template-data-service';
import { UserArea } from './UserArea';
import styles from './MainNavigation.module.scss';
import { MegaMenu } from './MegaMenu';
import { CategoryNavigation } from './CategoryNavigation';

import type { INavigationItem } from '../HeaderTypes';
import type { IUserWrapper } from '@/stores/currentUserApi';
import type * as Types from '@/pages/mijn-carel-lurvink/catalogus/stores/types';

export interface IMainNavigation {
  categories?: Types.Category[];
  isOpen: boolean;
  navigationItems: INavigationItem[];
  onClose: AppCallbackFn;
  trueFullScreen?: boolean;
  user?: IUserWrapper;
}

export const MainNavigation = ({ categories, navigationItems, user, isOpen, onClose, trueFullScreen = false }: IMainNavigation) => {
  const router = useRouter();
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const { t } = useTranslation('common');
  const breakpoint = useBreakpoint();
  const smallBreakpoint = ['xs', 'sm', 'md'].includes(breakpoint);
  const templateData = templateDataService.getTemplateData();

  useBrowserLayoutEffect(() => {
    if (smallBreakpoint) {
      setIsSmallScreen(true);
    } else {
      setIsSmallScreen(false);
    }
  }, [smallBreakpoint]);

  const handleMenuClose = () => {
    setIsCategoriesOpen(false);
    onClose();
  };

  return (
    <>
      <FullPageScrollPane
        isOpen={isOpen}
        trueFullScreen={trueFullScreen}
        {...(user && smallBreakpoint && { PageFooter: <UserArea user={user} /> })}
        {...(trueFullScreen && {
          PageHeader: (
            <div className="d-flex w-100 justify-content-end d-lg-none">
              <button type="button" className="btn btn-toggle ml-auto mr-3" onClick={handleMenuClose}>
                <span className="fa fa-times text-theme text-lg mr-1"></span>
                <span className="text-sans-serif text-gray-900 text-nowrap">{t('common:button.close')}</span>
              </button>
            </div>
          )
        })}
      >
        <ul className={`col px-0 mt-0 mt-lg-3 ${styles.mainNavigation}`}>
          {categories && !isSmallScreen && (
            <li className={`${styles.navigationItem} col-auto`}>
              <MegaMenu categories={categories} />
            </li>
          )}

          {categories && isSmallScreen && (
            <li className={`${styles.navigationItem} col-auto`}>
              <a role="button" className={`${styles.navigationLink}`} onClick={() => setIsCategoriesOpen(true)}>
                {t('common:main-navigation.Products')}
                <span className="cl-long-arrow-right"></span>
              </a>
              {categories && (
                <CategoryNavigation
                  categories={categories}
                  isOpen={isCategoriesOpen}
                  onClose={() => setIsCategoriesOpen(false)}
                  onMenuClose={handleMenuClose}
                />
              )}
            </li>
          )}

          {navigationItems?.map((item) => {
            const isCurrent = router.pathname.startsWith(item.href);

            const classNames = objStr({
              [styles.navigationItem]: true,
              [styles.active]: isCurrent,
              'col-auto': true
            });

            return (
              <li className={classNames} key={item.id}>
                <Link
                  data-testid={item.id}
                  href={item.href}
                  className={styles.navigationLink}
                  {...(item.target ? { target: item.target } : {})}
                >
                  {t(item.text)}
                </Link>
              </li>
            );
          })}
          {/* Do not display 'Beheer' on main site */}
          {!categories && (
            <li className={`${styles.navigationItem} col-auto ml-lg-auto p-xl-0`}>
              <a href={APP_ROUTES.MY_ACCOUNT} className={styles.navigationLink}>
                <span>
                  <FontAwesomeIcon icon={faCog} className="mr-2" />
                  {t('common:main-navigation.Manage')}
                </span>
              </a>
            </li>
          )}

          {categories && (
            <li className={`${styles.navigationItem} col-auto ml-lg-auto p-xl-0`}>
              <a href={`test://${templateData.sector.phone}`} className={styles.navigationLink}>
                <span>
                  <FontAwesomeIcon icon={faPhone} className="mr-2 text-theme" />
                  {templateData.sector.phone}
                </span>
              </a>
            </li>
          )}
        </ul>
      </FullPageScrollPane>
    </>
  );
};
